/**
 * メンバーの一覧・検索（issue #89・docs/01 15-1）。
 *
 * この画面の目的は「**名簿の中から、目当ての方を見つけて開く**」の1つだけです（docs/06 原則3）。
 * アカウントの停止・役職や拠点の変更は、開いた先の画面で行います。
 *
 * 絞り込みは、入力しただけでは動きません。**「この条件で探す」を押したときだけ**
 * 一覧を読み直します（docs/06 原則1「操作は明示のボタンで」）。
 *
 * 受け持ちが自拠点の方には、自拠点の方だけが出ます。これはサーバ側が決めています
 * （画面で隠しているのではありません・docs/01 5-5）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type { MemberChoices, MemberPage } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { MemberStatusTag, RoleTag } from '../components/MemberTags.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Button } from '../components/ui/Button.js';
import { DataTable } from '../components/ui/DataTable.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';

/** 1ページに出す人数。 */
const PAGE_SIZE = 25;

/** 絞り込みの条件（画面で入力しているもの）。 */
interface Filters {
  q: string;
  siteId: string;
  role: string;
  status: string;
}

const EMPTY_FILTERS: Filters = { q: '', siteId: '', role: '', status: '' };

export function MembersPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  // 入力中の条件と、いま一覧に効いている条件を分ける（押すまで一覧は変わらない）
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [page, setPage] = useState<MemberPage | null>(null);
  const [choices, setChoices] = useState<MemberChoices | null>(null);
  /** 拠点・役職の選択肢を読めなかったときの言葉（黙って空にしない・issue #207 L-2） */
  const [choicesError, setChoicesError] = useState<string | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const after = cursors[pageIndex];
  const canImport = profile?.grant.capabilities['roster.manage'] === true;

  // 絞り込みの選択肢（拠点・役職）は、画面を開いたときに一度だけ読む
  useEffect(() => {
    let cancelled = false;
    api
      .get<MemberChoices>('/admin/members/choices')
      .then((loaded) => {
        if (cancelled) return;
        setChoices(loaded);
        setChoicesError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // 選択肢が読めなくても、お名前での検索は使える（ここで画面は止めない）。
        // ただし**黙って空にはしない**。空のまま出すと「拠点が1つも無い会社」に見える
        setChoices({ sites: [], assignable_roles: [] });
        setChoicesError(caught instanceof ApiError ? caught.message : messageForStatus(0));
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);
    api
      .get<MemberPage>('/admin/members', {
        query: {
          limit: PAGE_SIZE,
          ...(applied.q !== '' ? { q: applied.q } : {}),
          ...(applied.siteId !== '' ? { site_id: applied.siteId } : {}),
          ...(applied.role !== '' ? { role: applied.role } : {}),
          ...(applied.status !== '' ? { status: applied.status } : {}),
          ...(after !== undefined ? { after } : {}),
        },
      })
      .then((loaded) => {
        if (!cancelled) setPage(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, applied, after, reloadKey]);

  /** 条件を変えたら、ページ送りの位置は先頭に戻す（前のページの続きから読まない）。 */
  function search(next: Filters): void {
    setApplied(next);
    setCursors([undefined]);
    setPageIndex(0);
  }

  function showNext(): void {
    const next = page?.nextCursor;
    if (!next) return;
    setCursors((prev) => [...prev.slice(0, pageIndex + 1), next]);
    setPageIndex((index) => index + 1);
  }

  return (
    <article>
      <PageHeader
        title="メンバー"
        description="メンバーを探して開きます。アカウントを無効にする・役職や拠点の変更は、開いた先で行います。"
        actions={
          canImport ? (
            <Link className="ui-button ui-button-secondary" to="/roster/import">
              CSVでまとめて登録
            </Link>
          ) : undefined
        }
      />

      <FormCard
        title="探す"
        submitLabel="この条件で探す"
        submitBusyLabel="探しています…"
        onSubmit={() => search(draft)}
        onCancel={() => {
          setDraft(EMPTY_FILTERS);
          search(EMPTY_FILTERS);
        }}
        cancelLabel="条件をすべて外す"
      >
        {choicesError ? (
          <ResultBanner
            tone="info"
            message={withReason(`拠点と役職の選択肢を読み込めませんでした`, choicesError)}
            nextStep="お名前・ふりがな・スタッフID・連絡先での検索はそのままお使いいただけます。拠点や役職で絞り込みたいときは、画面を開き直してください。"
          />
        ) : null}

        <div className="admin-filters">
          <label className="ui-field" htmlFor="members-q">
            <span>お名前・ふりがな・スタッフID・連絡先</span>
            <input
              id="members-q"
              type="search"
              value={draft.q}
              onChange={(event) => setDraft({ ...draft, q: event.target.value })}
            />
          </label>

          <label className="ui-field" htmlFor="members-site">
            <span>拠点</span>
            <select
              id="members-site"
              value={draft.siteId}
              onChange={(event) => setDraft({ ...draft, siteId: event.target.value })}
            >
              <option value="">すべての拠点</option>
              {choices?.sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
              {/* 拠点が決まっていない方は、受け持ちが自拠点の方には1人も出ない（issue #205） */}
              {profile?.grant.scope === 'org' ? (
                <option value="none">拠点が決まっていない方</option>
              ) : null}
            </select>
          </label>

          <label className="ui-field" htmlFor="members-role">
            <span>役職</span>
            <select
              id="members-role"
              value={draft.role}
              onChange={(event) => setDraft({ ...draft, role: event.target.value })}
            >
              <option value="">すべての役職</option>
              {choices?.assignable_roles.map((role) => (
                <option key={role.key} value={role.key}>
                  {role.display_name}
                </option>
              ))}
            </select>
          </label>

          <label className="ui-field" htmlFor="members-status">
            <span>在籍状態</span>
            <select
              id="members-status"
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
            >
              <option value="">すべて</option>
              <option value="active">利用中</option>
              <option value="invited">ログイン前</option>
              <option value="deactivated">無効</option>
            </select>
          </label>
        </div>
      </FormCard>

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !page ? <LoadingBlock label="メンバーを読み込んでいます…" /> : null}

      {page && page.items.length === 0 ? (
        <EmptyBlock label="この条件に当てはまる方はいませんでした。条件を外してお試しください。" />
      ) : null}

      {page && page.items.length > 0 ? (
        <DataTable label="メンバーの一覧">
          <thead>
            <tr>
              <th scope="col">お名前</th>
              <th scope="col">拠点</th>
              <th scope="col">役職</th>
              <th scope="col">在籍状態</th>
              <th scope="col">
                <span className="admin-visually-hidden">開く</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((member) => (
              <tr key={member.id}>
                <th scope="row">
                  {member.display_name}
                  {member.name_kana ? <small>{member.name_kana}</small> : null}
                  {member.staff_code ? <small>{member.staff_code}</small> : null}
                </th>
                <td className="ui-cell-soft">{member.site_name ?? '—'}</td>
                <td>
                  <RoleTag label={member.role_display_name} />
                </td>
                <td>
                  <MemberStatusTag status={member.status} />
                </td>
                <td>
                  <Link
                    className="ui-button ui-button-secondary ui-button-compact"
                    to={`/members/${member.id}`}
                  >
                    開く
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}

      <nav className="admin-pager" aria-label="ページ送り">
        <Button
          variant="secondary"
          compact
          onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
          disabled={pageIndex === 0}
        >
          前へ
        </Button>
        <span>{pageIndex + 1}ページ目</span>
        <Button variant="secondary" compact onClick={showNext} disabled={!page?.nextCursor}>
          次へ
        </Button>
      </nav>
    </article>
  );
}
